import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Divider,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  DragIndicator,
  Reorder,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationOnIcon,
  LinkedIn as LinkedInIcon,
  Language as LanguageIcon,
  DriveEta as DriveEtaIcon,
} from '@mui/icons-material';

// Default section order
const defaultSectionOrder = [
  'summary',
  'skills',
  'education',
  'experience',
  'interests',
  'certifications',
  'languages',
  'accomplishments',
  'additionalInfo',
  'softwareSkills',
  'volunteering',
  'websites',
  'references'
];

// A4 / Letter page pixel sizes (96 DPI) — used for pagination math
const A4_HEIGHT_PX = 1123;
const A4_WIDTH_PX = 794;
const LETTER_HEIGHT_PX = 1056;
const LETTER_WIDTH_PX = 816;

const Resume44 = ({
  theme = "#003087",
  color = "#003087",
  font = "Georgia",
  fontSize = "14px",
  fontStyle = "normal",
  headingSize = "18px",
  sectionSpacing = 30,
  paragraphSpacing = 12,
  lineSpacing = 22,
  topBottomMargin = 30,
  sideMargins = 30,
  pageSize = "A4",
  formData = {},
  exporting = false,
  enableDragDrop = true,
}) => {
  const [themeColor, setThemeColor] = useState(color || theme);
  const [currentPage, setCurrentPage] = useState(0);
  const resumeRef = useRef(null);

  // ── Pagination refs / state ──────────────────────────────────────────────
  const measureRef = useRef(null);
  const headerRef = useRef(null);
  const [pageBlocks, setPageBlocks] = useState([[]]); // array of arrays of block descriptors
  const [totalPages, setTotalPages] = useState(1);

  const pageH = pageSize === "A4" ? A4_HEIGHT_PX : LETTER_HEIGHT_PX;
  const pageW = pageSize === "A4" ? A4_WIDTH_PX : LETTER_WIDTH_PX;

  // State for all data sections
  const [personalData, setPersonalData] = useState({});
  const [savedSummary, setSavedSummary] = useState('');
  const [savedSkills, setSavedSkills] = useState([]);
  const [educationEntries, setEducationEntries] = useState([]);
  const [workExperiences, setWorkExperiences] = useState([]);
  const [savedCertifications, setSavedCertifications] = useState([]);
  const [savedLanguages, setSavedLanguages] = useState([]);
  const [savedAccomplishments, setSavedAccomplishments] = useState([]);
  const [savedAdditionalInfo, setSavedAdditionalInfo] = useState([]);
  const [savedSoftware, setSavedSoftware] = useState([]);
  const [savedReferences, setSavedReferences] = useState([]);
  const [savedVolunteering, setSavedVolunteering] = useState([]);
  const [savedInterests, setSavedInterests] = useState([]);
  const [savedWebsites, setSavedWebsites] = useState([]);
  const [customSections, setCustomSections] = useState({});

  // Section ordering states
  const [sectionOrder, setSectionOrder] = useState(defaultSectionOrder);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [arrangeDialogOpen, setArrangeDialogOpen] = useState(false);

  // Global font style
  const globalFontStyle = {
    fontFamily: font,
    fontSize: fontSize,
    fontStyle: fontStyle,
    lineHeight: `${lineSpacing}px`,
  };

  const getTextStyle = (additionalStyles = {}) => ({
    ...globalFontStyle,
    ...additionalStyles
  });

  // Load all data from localStorage
  useEffect(() => {
    setThemeColor(color || theme);

    // Load personal data
    const storedPersonalData = localStorage.getItem("personalData");
    let personal = {};
    
    if (storedPersonalData) {
      try {
        personal = JSON.parse(storedPersonalData);
      } catch (e) {
        console.error("Error parsing personalData:", e);
      }
    }
    
    if (formData && Object.keys(formData).length > 0) {
      personal = { ...personal, ...formData };
    }
    
    setPersonalData(personal);

    // Load summary
    const storedSummaries = localStorage.getItem("summaries");
    if (storedSummaries) {
      try {
        const summaries = JSON.parse(storedSummaries);
        if (Array.isArray(summaries) && summaries.length > 0) {
          setSavedSummary(summaries[summaries.length - 1]);
        }
      } catch (e) {
        console.error("Error parsing summaries:", e);
      }
    }

    // Load education
    const storedEntries = localStorage.getItem("educationEntries");
    if (storedEntries) {
      try {
        setEducationEntries(JSON.parse(storedEntries));
      } catch (e) {
        console.error("Error parsing educationEntries:", e);
      }
    }

    // Load skills
    const storedSkills = localStorage.getItem("skills");
    if (storedSkills) {
      try {
        setSavedSkills(JSON.parse(storedSkills));
      } catch (e) {
        console.error("Error parsing skills:", e);
      }
    }

    // Load work experiences
    const storedWorkExperiences = localStorage.getItem("workExperiences");
    if (storedWorkExperiences) {
      try {
        setWorkExperiences(JSON.parse(storedWorkExperiences) || []);
      } catch (e) {
        console.error("Error parsing workExperiences:", e);
      }
    }

    // Load websites
    const websites = localStorage.getItem("websites");
    if (websites) {
      try {
        setSavedWebsites(JSON.parse(websites) || []);
      } catch (e) {
        console.error("Error parsing websites:", e);
      }
    }

    // Load certifications
    const certifications = localStorage.getItem("certificationsList");
    if (certifications) {
      try {
        setSavedCertifications(JSON.parse(certifications) || []);
      } catch (e) {
        console.error("Error parsing certifications:", e);
      }
    }

    // Load languages
    const languages = localStorage.getItem("languagesList");
    if (languages) {
      try {
        setSavedLanguages(JSON.parse(languages) || []);
      } catch (e) {
        console.error("Error parsing languages:", e);
      }
    }

    // Load accomplishments
    const accomplishments = localStorage.getItem("accomplishmentsList");
    if (accomplishments) {
      try {
        setSavedAccomplishments(JSON.parse(accomplishments) || []);
      } catch (e) {
        console.error("Error parsing accomplishments:", e);
      }
    }

    // Load additional info
    const additionalInfo = localStorage.getItem("additionalInfo");
    if (additionalInfo) {
      try {
        setSavedAdditionalInfo(JSON.parse(additionalInfo) || []);
      } catch (e) {
        console.error("Error parsing additionalInfo:", e);
      }
    }

    // Load software skills
    const software = localStorage.getItem("softwareSkills");
    if (software) {
      try {
        setSavedSoftware(JSON.parse(software) || []);
      } catch (e) {
        console.error("Error parsing softwareSkills:", e);
      }
    }

    // Load references
    const references = localStorage.getItem("referencesList");
    if (references) {
      try {
        setSavedReferences(JSON.parse(references) || []);
      } catch (e) {
        console.error("Error parsing references:", e);
      }
    }

    // Load volunteering
    const volunteering = localStorage.getItem("volunteering");
    if (volunteering) {
      try {
        setSavedVolunteering(JSON.parse(volunteering) || []);
      } catch (e) {
        console.error("Error parsing volunteering:", e);
      }
    }

    // Load interests
    const interests = localStorage.getItem("interests");
    if (interests) {
      try {
        setSavedInterests(JSON.parse(interests) || []);
      } catch (e) {
        console.error("Error parsing interests:", e);
      }
    }

    // Load custom sections
    const userId = localStorage.getItem("userId");
    let sectionsData = {};

    if (userId) {
      const currentCustomSections = localStorage.getItem(`current_custom_sections_${userId}`);
      
      if (currentCustomSections) {
        try {
          const customSectionsList = JSON.parse(currentCustomSections);
          
          if (Array.isArray(customSectionsList) && customSectionsList.length > 0) {
            customSectionsList.forEach(sectionName => {
              const userCustomKey = `custom_${userId}_${sectionName}`;
              try {
                const customSectionData = localStorage.getItem(userCustomKey);
                if (customSectionData) {
                  const parsedData = JSON.parse(customSectionData);
                  const items = parsedData.items || [];
                  const validItems = items.filter(item => item && item.trim() !== '');
                  
                  if (validItems.length > 0) {
                    sectionsData[sectionName] = validItems;
                  }
                }
              } catch (error) {
                console.error(`Error loading custom section ${sectionName}:`, error);
              }
            });
          }
        } catch (e) {
          console.error("Error parsing custom sections:", e);
        }
      }
    }

    setCustomSections(sectionsData);

    // Load saved section order
    const savedSectionOrder = localStorage.getItem("resume35SectionOrder");
    if (savedSectionOrder) {
      try {
        const parsedOrder = JSON.parse(savedSectionOrder);
        const currentCustomSectionIds = Object.keys(sectionsData).map(name => `custom_${name}`);
        const filteredOrder = parsedOrder.filter(section => !section.startsWith('custom_'));
        const allSections = [...filteredOrder, ...currentCustomSectionIds];
        setSectionOrder(allSections);
      } catch (e) {
        console.error("Error parsing section order:", e);
        const currentCustomSectionIds = Object.keys(sectionsData).map(name => `custom_${name}`);
        setSectionOrder([...defaultSectionOrder, ...currentCustomSectionIds]);
      }
    } else {
      const currentCustomSectionIds = Object.keys(sectionsData).map(name => `custom_${name}`);
      const updatedSectionOrder = [...defaultSectionOrder, ...currentCustomSectionIds];
      setSectionOrder(updatedSectionOrder);
    }
  }, [color, theme, formData]);

  // Update section order when custom sections change
  useEffect(() => {
    const userId = localStorage.getItem("userId");
    if (userId && Object.keys(customSections).length > 0) {
      const currentOrder = [...sectionOrder];
      const currentCustomSectionIds = Object.keys(customSections).map(name => `custom_${name}`);
      const filteredOrder = currentOrder.filter(section => !section.startsWith('custom_'));
      const newOrder = [...filteredOrder, ...currentCustomSectionIds];
      
      if (JSON.stringify(newOrder) !== JSON.stringify(currentOrder)) {
        setSectionOrder(newOrder);
        localStorage.setItem("resume35SectionOrder", JSON.stringify(newOrder));
      }
    }
  }, [customSections]);

  // Save section order to localStorage
  useEffect(() => {
    if (sectionOrder.length > 0) {
      localStorage.setItem("resume35SectionOrder", JSON.stringify(sectionOrder));
    }
  }, [sectionOrder]);

  const formatDate = (month, year) => {
    if (!month || !year) return "";
    return `${month} ${year}`;
  };

  // Drag and drop handlers
  const handleDragStart = (e, index) => {
    e.dataTransfer.setData('text/plain', JSON.stringify({ index, sectionId: sectionOrder[index] }));
    e.currentTarget.style.opacity = '0.5';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e, targetIndex) => {
    e.preventDefault();
    const data = JSON.parse(e.dataTransfer.getData('text/plain'));
    const sourceIndex = data.index;

    if (sourceIndex !== targetIndex) {
      const newSectionOrder = [...sectionOrder];
      const [movedSection] = newSectionOrder.splice(sourceIndex, 1);
      newSectionOrder.splice(targetIndex, 0, movedSection);
      setSectionOrder(newSectionOrder);
    }

    setDragOverIndex(null);
    e.currentTarget.style.opacity = '1';
  };

  const handleDragEnd = (e) => {
    setDragOverIndex(null);
    e.currentTarget.style.opacity = '1';
  };

  // ── Reset All Sections (single function used everywhere) ────────────────
  const resetSectionOrder = () => {
    const currentCustomSectionIds = Object.keys(customSections).map(name => `custom_${name}`);
    const newOrder = [...defaultSectionOrder, ...currentCustomSectionIds];
    setSectionOrder(newOrder);
    localStorage.setItem("resume35SectionOrder", JSON.stringify(newOrder));
    setCurrentPage(0);
  };

  const getSectionTitle = (sectionId) => {
    const titles = {
      'summary': 'Profile Summary',
      'skills': 'Professional Skill',
      'experience': 'Work Experience',
      'education': 'Education',
      'certifications': 'Certifications',
      'languages': 'Languages',
      'accomplishments': 'Accomplishments',
      'additionalInfo': 'Additional Information',
      'softwareSkills': 'Software Skills',
      'volunteering': 'Volunteering',
      'interests': 'Interest',
      'websites': 'Websites & Profiles',
      'references': 'References'
    };

    if (sectionId.startsWith('custom_')) {
      const customName = sectionId.replace('custom_', '').replace(/_/g, ' ');
      return customName.toUpperCase();
    }
    
    return titles[sectionId] || sectionId.toUpperCase();
  };

  const renderSectionContent = (sectionId) => {
    switch (sectionId) {
      case 'summary':
        return savedSummary ? (
          <Box sx={{ mb: `${sectionSpacing}px` }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: themeColor,
                mb: 1.5,
                textTransform: 'uppercase',
                letterSpacing: '1px',
                ...globalFontStyle,
                fontSize: headingSize
              }}
            >
              Profile Summary
            </Typography>
            <Typography sx={{ lineHeight: 1.9, fontSize: '0.98rem', mb: `${paragraphSpacing}px`, ...globalFontStyle }}>
              {savedSummary}
            </Typography>
          </Box>
        ) : null;

      case 'skills':
        return savedSkills.length > 0 ? (
          <Box sx={{ mb: `${sectionSpacing}px` }}>
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: themeColor,
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  ...globalFontStyle,
                  fontSize: headingSize
                }}
              >
                Professional Skill
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 4 }}>
              <Box sx={{ flex: 1 }}>
                <Typography component="ul" sx={{ pl: 3, lineHeight: 2.1, fontSize: '0.97rem', mb: `${paragraphSpacing}px`, ...globalFontStyle }}>
                  {savedSkills.slice(0, Math.ceil(savedSkills.length / 2)).map((skill, index) => (
                    <li key={index}>{skill.name}</li>
                  ))}
                </Typography>
              </Box>

              <Box sx={{ flex: 1 }}>
                <Typography component="ul" sx={{ pl: 3, lineHeight: 2.1, fontSize: '0.97rem', mb: `${paragraphSpacing}px`, ...globalFontStyle }}>
                  {savedSkills.slice(Math.ceil(savedSkills.length / 2)).map((skill, index) => (
                    <li key={index}>{skill.name}</li>
                  ))}
                </Typography>
              </Box>
            </Box>
          </Box>
        ) : null;

      case 'education':
        return educationEntries.length > 0 ? (
          <Box sx={{ mb: `${sectionSpacing}px` }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: themeColor,
                mb: 2,
                textTransform: 'uppercase',
                letterSpacing: '1px',
                ...globalFontStyle,
                fontSize: headingSize
              }}
            >
              Education
            </Typography>
            {educationEntries.map((edu, index) => {
              const institutionName = edu.educationLevel === "school" ? edu.schoolName : edu.collegeName;
              const degreeName = edu.educationLevel === "school" ? edu.standard : edu.degreeName;
              const fieldOfStudy = edu.educationLevel === "school" ? edu.stream : edu.specialization;
              
              return (
                <Box key={index} sx={{ mb: `${paragraphSpacing}px` }}>
                  <Typography sx={{ fontWeight: 700, color: '#000', ...globalFontStyle }}>
                    {degreeName} {institutionName}
                  </Typography>
                  {fieldOfStudy && (
                    <Typography sx={{ color: '#555', fontStyle: 'italic', mb: 0.5, ...globalFontStyle }}>
                      {fieldOfStudy}
                    </Typography>
                  )}
                  <Typography sx={{ color: '#555', ...globalFontStyle }}>
                    {formatDate(edu.gradMonth, edu.gradYear)}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        ) : null;

      case 'experience':
        return workExperiences.length > 0 ? (
          <Box sx={{ mb: `${sectionSpacing}px` }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: themeColor,
                mb: 2,
                textTransform: 'uppercase',
                letterSpacing: '1px',
                ...globalFontStyle,
                fontSize: headingSize
              }}
            >
              Work Experience
            </Typography>
            {workExperiences.map((work, index) => (
              <Box key={index} sx={{ mb: `${paragraphSpacing}px` }}>
                <Typography sx={{ fontWeight: 700, color: '#000', mb: 0.5, ...globalFontStyle }}>
                  {work.jobTitle}
                </Typography>
                <Typography sx={{ color: '#555', fontStyle: 'italic', mb: 1, ...globalFontStyle }}>
                  {formatDate(work.startMonth, work.startYear)} – {work.current ? "Present" : formatDate(work.endMonth, work.endYear)}
                </Typography>
                {work.description && (
                  <Typography component="ul" sx={{ pl: 3, lineHeight: 2, fontSize: '0.97rem', ...globalFontStyle }}>
                    {(work.description || '').split('\n').map((line, i) => (
                      line.trim().startsWith('•') ? (
                        <li key={i}>{line.replace('•', '').trim()}</li>
                      ) : line.trim() ? (
                        <Typography key={i} sx={globalFontStyle}>{line}</Typography>
                      ) : null
                    ))}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        ) : null;

      case 'interests':
        return savedInterests.length > 0 ? (
          <Box sx={{ mb: `${sectionSpacing}px` }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: themeColor,
                mb: 2,
                textTransform: 'uppercase',
                letterSpacing: '1px',
                ...globalFontStyle,
                fontSize: headingSize
              }}
            >
              Interest
            </Typography>
            <Typography component="ul" sx={{ pl: 3, lineHeight: 2.1, fontSize: '0.97rem', mb: `${paragraphSpacing}px`, ...globalFontStyle }}>
              {savedInterests.map((interest, index) => (
                <li key={index}>{interest}</li>
              ))}
            </Typography>
          </Box>
        ) : null;

      case 'certifications':
        return savedCertifications.length > 0 ? (
          <Box sx={{ mb: `${sectionSpacing}px` }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: themeColor,
                mb: 2,
                textTransform: 'uppercase',
                letterSpacing: '1px',
                ...globalFontStyle,
                fontSize: headingSize
              }}
            >
              Certifications
            </Typography>
            {savedCertifications.map((cert, index) => (
              <Box key={index} sx={{ mb: `${paragraphSpacing}px` }}>
                <Typography sx={{ fontWeight: 700, color: '#000', ...globalFontStyle }}>
                  {cert.name}
                </Typography>
                <Typography sx={{ color: '#555', ...globalFontStyle }}>
                  {cert.provider} • {cert.year}
                </Typography>
              </Box>
            ))}
          </Box>
        ) : null;

      case 'languages':
        return savedLanguages.length > 0 ? (
          <Box sx={{ mb: `${sectionSpacing}px` }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: themeColor,
                mb: 2,
                textTransform: 'uppercase',
                letterSpacing: '1px',
                ...globalFontStyle,
                fontSize: headingSize
              }}
            >
              Languages
            </Typography>
            <Typography component="ul" sx={{ pl: 3, lineHeight: 2.1, fontSize: '0.97rem', mb: `${paragraphSpacing}px`, ...globalFontStyle }}>
              {savedLanguages.map((lang, index) => (
                <li key={index}>{lang.name} {lang.level && `- ${lang.level}`}</li>
              ))}
            </Typography>
          </Box>
        ) : null;

      case 'accomplishments':
        return savedAccomplishments.length > 0 ? (
          <Box sx={{ mb: `${sectionSpacing}px` }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: themeColor,
                mb: 2,
                textTransform: 'uppercase',
                letterSpacing: '1px',
                ...globalFontStyle,
                fontSize: headingSize
              }}
            >
              Accomplishments
            </Typography>
            <Typography component="ul" sx={{ pl: 3, lineHeight: 2.1, fontSize: '0.97rem', mb: `${paragraphSpacing}px`, ...globalFontStyle }}>
              {savedAccomplishments.map((acc, index) => (
                <li key={index}>{acc}</li>
              ))}
            </Typography>
          </Box>
        ) : null;

      case 'additionalInfo':
        return savedAdditionalInfo.length > 0 ? (
          <Box sx={{ mb: `${sectionSpacing}px` }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: themeColor,
                mb: 2,
                textTransform: 'uppercase',
                letterSpacing: '1px',
                ...globalFontStyle,
                fontSize: headingSize
              }}
            >
              Additional Information
            </Typography>
            <Typography component="ul" sx={{ pl: 3, lineHeight: 2.1, fontSize: '0.97rem', mb: `${paragraphSpacing}px`, ...globalFontStyle }}>
              {savedAdditionalInfo.map((info, index) => (
                <li key={index}>{info}</li>
              ))}
            </Typography>
          </Box>
        ) : null;

      case 'softwareSkills':
        return savedSoftware.length > 0 ? (
          <Box sx={{ mb: `${sectionSpacing}px` }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: themeColor,
                mb: 2,
                textTransform: 'uppercase',
                letterSpacing: '1px',
                ...globalFontStyle,
                fontSize: headingSize
              }}
            >
              Software Skills
            </Typography>
            <Typography component="ul" sx={{ pl: 3, lineHeight: 2.1, fontSize: '0.97rem', mb: `${paragraphSpacing}px`, ...globalFontStyle }}>
              {savedSoftware.map((software, index) => (
                <li key={index}>{software.name} - {software.level}%</li>
              ))}
            </Typography>
          </Box>
        ) : null;

      case 'volunteering':
        return savedVolunteering.length > 0 ? (
          <Box sx={{ mb: `${sectionSpacing}px` }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: themeColor,
                mb: 2,
                textTransform: 'uppercase',
                letterSpacing: '1px',
                ...globalFontStyle,
                fontSize: headingSize
              }}
            >
              Volunteering
            </Typography>
            {savedVolunteering.map((vol, index) => (
              <Box key={index} sx={{ mb: `${paragraphSpacing}px` }}>
                <Typography sx={{ fontWeight: 700, ...globalFontStyle }}>{vol.subtopic}</Typography>
                <Typography sx={{ fontStyle: 'italic', color: '#555', ...globalFontStyle }}>
                  {vol.fromDate} - {vol.toDate}
                </Typography>
                <Typography sx={{ lineHeight: 1.8, ...globalFontStyle }}>{vol.content}</Typography>
              </Box>
            ))}
          </Box>
        ) : null;

      case 'websites':
        return savedWebsites.length > 0 ? (
          <Box sx={{ mb: `${sectionSpacing}px` }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: themeColor,
                mb: 2,
                textTransform: 'uppercase',
                letterSpacing: '1px',
                ...globalFontStyle,
                fontSize: headingSize
              }}
            >
              Websites & Profiles
            </Typography>
            <Typography component="ul" sx={{ pl: 3, lineHeight: 2.1, fontSize: '0.97rem', mb: `${paragraphSpacing}px`, ...globalFontStyle }}>
              {savedWebsites.map((site, index) => (
                <li key={index}>{site.url}</li>
              ))}
            </Typography>
          </Box>
        ) : null;

      case 'references':
        return savedReferences.length > 0 ? (
          <Box sx={{ mb: `${sectionSpacing}px` }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: themeColor,
                mb: 2,
                textTransform: 'uppercase',
                letterSpacing: '1px',
                ...globalFontStyle,
                fontSize: headingSize
              }}
            >
              References
            </Typography>
            <Typography component="ul" sx={{ pl: 3, lineHeight: 2.1, fontSize: '0.97rem', mb: `${paragraphSpacing}px`, ...globalFontStyle }}>
              {savedReferences.map((ref, index) => (
                <li key={index}>{ref}</li>
              ))}
            </Typography>
          </Box>
        ) : null;

      default:
        if (sectionId && sectionId.startsWith('custom_')) {
          const customSectionName = sectionId.replace('custom_', '');
          const items = customSections[customSectionName] || [];
          const validItems = items.filter(item => item && item.trim() !== '');
          
          if (validItems.length > 0) {
            return (
              <Box sx={{ mb: `${sectionSpacing}px` }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    color: themeColor,
                    mb: 2,
                    textTransform: 'uppercase',
                    letterSpacing: '1px',
                    ...globalFontStyle,
                    fontSize: headingSize
                  }}
                >
                  {customSectionName.replace(/_/g, ' ').toUpperCase()}
                </Typography>
                <Typography component="ul" sx={{ pl: 3, lineHeight: 2.1, fontSize: '0.97rem', mb: `${paragraphSpacing}px`, ...globalFontStyle }}>
                  {validItems.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </Typography>
              </Box>
            );
          }
        }
        return null;
    }
  };

  const renderDragPanel = () => {
    return (
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
          <DragIndicator sx={{ mr: 1 }} />
          Resume Sections
          <Typography variant="body2" color="textSecondary" sx={{ ml: 1 }}>
            ({sectionOrder.length} sections)
          </Typography>
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Drag and drop to reorder sections.
        </Typography>
        <Box sx={{ maxHeight: 400, overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
          {sectionOrder.map((sectionId, index) => (
            <Paper
              key={sectionId}
              draggable
              onDragStart={(e) => handleDragStart(e, index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, index)}
              onDragEnd={handleDragEnd}
              sx={{
                p: 2,
                mb: 1,
                display: 'flex',
                alignItems: 'center',
                backgroundColor: dragOverIndex === index ? '#f0f0f0' : 'white',
                boxShadow: dragOverIndex === index ? 2 : 1,
                cursor: 'grab',
                '&:active': { cursor: 'grabbing' },
                transition: 'all 0.2s ease',
              }}
            >
              <DragIndicator color="action" />
              <Typography sx={{ ml: 1, flexGrow: 1 }}>
                {getSectionTitle(sectionId)}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {index + 1}
              </Typography>
            </Paper>
          ))}
        </Box>
        <Button onClick={resetSectionOrder} variant="outlined" size="small" sx={{ mt: 1 }}>
          Reset All Sections
        </Button>
      </Box>
    );
  };

  // ── HEADER (rendered once, only on page 1) ──────────────────────────────
  const renderHeader = () => (
    <Box sx={{ textAlign: 'center', mb: 4 }}>
      <Typography
        variant="h3"
        component="h1"
        sx={{
          fontWeight: 900,
          letterSpacing: '3px',
          color: themeColor,
          fontSize: '2.9rem',
          lineHeight: 1.1,
          mb: 1,
          ...globalFontStyle
        }}
      >
        {(personalData.firstName?.toUpperCase() || 'DREW')} {(personalData.lastName?.toUpperCase() || 'FEIG')}
      </Typography>

      <Typography
        variant="h6"
        sx={{
          fontWeight: 700,
          color: themeColor,
          letterSpacing: '1.5px',
          fontSize: '1.4rem',
          textTransform: 'uppercase',
          mb: 2,
          ...globalFontStyle
        }}
      >
        {personalData.currentPosition || 'MARKETING SPECIALIST'}
      </Typography>

      {/* Contact Info - First Row */}
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap', mb: 1 }}>
        {personalData.phone && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <PhoneIcon sx={{ fontSize: 16, color: '#555' }} />
            <Typography sx={{ fontSize: '1rem', color: '#444', ...globalFontStyle }}>
              {personalData.phone}
            </Typography>
          </Box>
        )}
        {personalData.email && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <EmailIcon sx={{ fontSize: 16, color: '#555' }} />
            <Typography sx={{ fontSize: '1rem', color: '#444', ...globalFontStyle }}>
              {personalData.email}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Location */}
      {personalData.city && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 0.5, mb: 1 }}>
          <LocationOnIcon sx={{ fontSize: 16, color: '#555' }} />
          <Typography sx={{ fontSize: '1rem', color: '#444', ...globalFontStyle }}>
            {personalData.city}
          </Typography>
        </Box>
      )}

      {/* LinkedIn, Website, Driving Licence */}
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap', mt: 1 }}>
        {personalData.linkedIn && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <LinkedInIcon sx={{ fontSize: 16, color: '#0077b5' }} />
            <a 
              href={personalData.linkedIn.startsWith('http') ? personalData.linkedIn : `https://${personalData.linkedIn}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ 
                color: '#0077b5', 
                textDecoration: 'none',
                fontSize: '1rem',
                fontFamily: font
              }}
            >
              {personalData.linkedIn.replace(/^https?:\/\/(www\.)?linkedin\.com\/in\/|linkedin\.com\/in\//, '')}
            </a>
          </Box>
        )}
        
        {personalData.website && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <LanguageIcon sx={{ fontSize: 16, color: '#1976d2' }} />
            <a 
              href={personalData.website.startsWith('http') ? personalData.website : `https://${personalData.website}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ 
                color: '#1976d2', 
                textDecoration: 'none',
                fontSize: '1rem',
                fontFamily: font
              }}
            >
              {personalData.website.replace(/^https?:\/\/(www\.)?/, '')}
            </a>
          </Box>
        )}
        
        {personalData.drivingLicence && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <DriveEtaIcon sx={{ fontSize: 16, color: '#4caf50' }} />
            <Typography sx={{ fontSize: '1rem', color: '#555', ...globalFontStyle }}>
              DL: {personalData.drivingLicence}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );

  // ── Build ordered list of blocks: sections (with content) + dividers ────
  const buildBlocks = () => {
    const visibleSections = sectionOrder.filter((id) => renderSectionContent(id) !== null);
    const blocks = [];
    visibleSections.forEach((id, idx) => {
      blocks.push({ type: 'section', id, key: `sec-${id}` });
      if (idx < visibleSections.length - 1) {
        blocks.push({ type: 'divider', id: `divider-${idx}`, key: `div-${idx}` });
      }
    });
    return blocks;
  };

  const renderBlock = (block) => {
    if (block.type === 'divider') {
      return <Divider sx={{ borderColor: '#888', my: 4 }} />;
    }
    return renderSectionContent(block.id);
  };

  // ── Pagination calculation ───────────────────────────────────────────────
  const paginateContent = useCallback(() => {
    const container = measureRef.current;
    const headerEl = headerRef.current;
    if (!container || !headerEl) return;

    const usableHeight = pageH - (2 * topBottomMargin);
    const headerHeight = headerEl.offsetHeight;

    const blockEls = Array.from(container.querySelectorAll('.resume44-block'));

    let pages = [[]];
    let pageIdx = 0;
    let accumulated = headerHeight; // page 1 starts with header already consuming space

    blockEls.forEach((el, i) => {
      const h = el.offsetHeight;
      const capacity = pageIdx === 0 ? usableHeight : usableHeight;
      const baseUsed = pageIdx === 0 ? headerHeight : 0;

      if (accumulated + h > usableHeight && pages[pageIdx].length > 0) {
        // Don't start a new page with a divider
        if (allBlocksRef.current[i] && allBlocksRef.current[i].type === 'divider') {
          // skip the divider entirely at page break
          accumulated = pageIdx === 0 ? 0 : 0;
          pageIdx++;
          pages.push([]);
          accumulated = 0;
          return;
        }
        pageIdx++;
        pages.push([]);
        accumulated = 0;
      }

      pages[pageIdx].push(allBlocksRef.current[i]);
      accumulated += h;
    });

    // Remove empty trailing pages
    pages = pages.filter((p, idx) => p.length > 0 || idx === 0);

    setPageBlocks(pages);
    setTotalPages(Math.max(1, pages.length));
  }, [pageH, topBottomMargin]);

  // Keep a ref to the current blocks list so paginateContent can access it
  const allBlocksRef = useRef([]);

  useEffect(() => {
    if (exporting) return;
    allBlocksRef.current = buildBlocks();
    const id = setTimeout(() => {
      paginateContent();
    }, 400);
    return () => clearTimeout(id);
  }, [
    sectionOrder, savedSummary, savedSkills, educationEntries, workExperiences,
    savedCertifications, savedLanguages, savedAccomplishments, savedAdditionalInfo,
    savedSoftware, savedReferences, savedVolunteering, savedInterests, savedWebsites,
    customSections, personalData, sectionSpacing, paragraphSpacing, lineSpacing,
    fontSize, headingSize, topBottomMargin, sideMargins, pageH, exporting, paginateContent,
  ]);

  // Clamp currentPage if totalPages shrinks
  useEffect(() => {
    if (currentPage > totalPages - 1) {
      setCurrentPage(Math.max(0, totalPages - 1));
    }
  }, [totalPages, currentPage]);

  const displayPages = pageBlocks;
  const numDisplayPages = totalPages;
  const pageHeight = pageSize === "A4" ? "297mm" : "279mm";
  const pageWidth = pageSize === "A4" ? "210mm" : "216mm";

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#f8f9fa',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        p: 3,
      }}
    >
      {!exporting && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
          <IconButton onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))} disabled={currentPage === 0} color="primary">
            <ChevronLeft />
          </IconButton>
          <Typography>Page {currentPage + 1} of {numDisplayPages}</Typography>
          <IconButton onClick={() => setCurrentPage(prev => Math.min(numDisplayPages - 1, prev + 1))} disabled={currentPage >= numDisplayPages - 1} color="primary">
            <ChevronRight />
          </IconButton>
          
          {enableDragDrop && (
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <Button onClick={() => setArrangeDialogOpen(true)} variant="contained" startIcon={<Reorder />}>
                Arrange Sections
              </Button>
              <Button onClick={resetSectionOrder} variant="outlined" color="secondary" size="small">
                Reset All Sections
              </Button>
            </Box>
          )}
        </Box>
      )}

      {/* ── Hidden measurement container (header + all blocks) ── */}
      {!exporting && (
        <Box
          sx={{
            position: 'absolute',
            visibility: 'hidden',
            pointerEvents: 'none',
            top: 0,
            left: 0,
            zIndex: -1,
            width: `${pageW - 2 * sideMargins}px`,
            fontFamily: "'Georgia', 'Times New Roman', serif",
          }}
        >
          <Box ref={headerRef}>
            {renderHeader()}
            <Divider sx={{ borderColor: themeColor, borderWidth: 1, my: 3 }} />
          </Box>
          <Box ref={measureRef}>
            {allBlocksRef.current.map((block) => (
              <Box className="resume44-block" key={block.key}>
                {renderBlock(block)}
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* ── Rendered pages ── */}
      {Array.from({ length: numDisplayPages }, (_, i) => i).map((pageIdx) => (
        <Paper
          key={pageIdx}
          className="resume-page"
          elevation={exporting ? 0 : 3}
          ref={pageIdx === 0 ? resumeRef : null}
          sx={{
            width: pageWidth,
            height: exporting ? pageHeight : 'auto',
            minHeight: pageHeight,
            bgcolor: 'white',
            p: `${topBottomMargin}px ${sideMargins}px`,
            mb: exporting ? 0 : 3,
            fontFamily: "'Georgia', 'Times New Roman', serif",
            color: '#1a1a1a',
            boxSizing: 'border-box',
            display: exporting ? 'block' : (pageIdx === currentPage ? 'block' : 'none'),
            pageBreakAfter: 'always',
            '@media print': {
              width: pageWidth,
              height: pageHeight,
              minHeight: pageHeight,
              p: `${topBottomMargin}px ${sideMargins}px`,
              boxShadow: 'none',
              margin: 0,
              display: 'block',
            }
          }}
        >
          {/* Header only on first page */}
          {pageIdx === 0 && (
            <>
              {renderHeader()}
              <Divider sx={{ borderColor: themeColor, borderWidth: 1, my: 3 }} />
            </>
          )}

          {/* Render blocks for this page */}
          {(displayPages[pageIdx] || []).map((block) => (
            <React.Fragment key={block.key}>
              {renderBlock(block)}
            </React.Fragment>
          ))}
        </Paper>
      ))}

      {/* Arrange Sections Dialog */}
      <Dialog open={arrangeDialogOpen} onClose={() => setArrangeDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <Reorder sx={{ mr: 1 }} />
            Arrange Resume Sections
          </Box>
        </DialogTitle>
        <DialogContent>
          {renderDragPanel()}
        </DialogContent>
        <DialogActions>
          <Button onClick={resetSectionOrder} color="secondary">Reset All Sections</Button>
          <Button onClick={() => setArrangeDialogOpen(false)} variant="contained">Done</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export const resumeMeta = {
  hasPhoto: false,
  columns: 1,
};

export default Resume44;